import React from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useMerchantConfigs } from '../../hooks/useMerchantConfig';
import { cardnetStatus, cybersourceStatus, maskTail, type GatewayChoice, type GatewayStatus } from './gatewaySettings';

/**
 * SAM-1168 — Business › Settings › Advanced › Payment gateway (canvas v157, GatewayAdvanced / GatewayStates).
 * Only xhinola_admin gets this card (the caller decides; the server re-checks). Cardnet is merchant + terminal in
 * business.settings and the choice is settings.payment_gateway: all of that saves with the page's Save bar.
 * CyberSource is a pointer (merchant_config: secret name + mode); it is read here and changed in
 * Settings › Merchant configuration, which already knows the environment of the row. Keys are never shown.
 */

export const GATEWAY_LABEL: Record<GatewayChoice, string> = { cardnet: 'Cardnet', cybersource: 'CyberSource' };

type Props = {
  businessId: string;
  gateway: GatewayChoice | '';
  merchantId: string;
  terminalId: string;
  editing: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onChange: (patch: { gateway?: GatewayChoice; merchantId?: string; terminalId?: string }) => void;
};

const row = 'grid grid-cols-[150px_minmax(0,1fr)] items-center gap-3 text-sm min-h-[38px]';
const input = 'h-10 px-3 rounded-lg border border-solid border-[var(--line)] bg-white text-sm';

const Chip: React.FC<{ status: GatewayStatus }> = ({ status }) => (
  <span
    className={`text-xs font-semibold px-2.5 py-[3px] rounded-full ${status.tone === 'ok'
      ? 'bg-[var(--ok-bg)] text-[var(--ok-fg)]'
      : 'bg-[var(--warn-bg-2)] text-[var(--warn-fg)]'}`}
  >
    {status.label}
  </span>
);

const NotSet: React.FC = () => <span className="text-[var(--warn-fg)]">Not set</span>;

export const PaymentGatewayCard: React.FC<Props> = ({
  businessId, gateway, merchantId, terminalId, editing, onEdit, onCancelEdit, onChange,
}) => {
  const { data: configs, isError: configsFailed } = useMerchantConfigs(true);
  const own = (configs || []).filter((c) => c.active && c.business_id === businessId);
  const secret = own[0];

  return (
    <section aria-label="Payment gateway" className="rounded-xl border border-solid border-[var(--line)] bg-white p-5 flex flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h2 className="m-0 text-[15px] font-bold">Payment gateway</h2>
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--ink-500)]">
            <Lock size={12} aria-hidden="true" />
            Only xhinola_admin sees this
          </span>
        </div>
        {editing ? (
          <button type="button" className="h-8 px-3 rounded-lg border border-solid border-[var(--line)] bg-white text-[13px] font-semibold cursor-pointer" onClick={onCancelEdit}>
            Cancel
          </button>
        ) : (
          <button type="button" className="h-8 px-3 rounded-lg border border-solid border-[var(--line)] bg-white text-[13px] font-semibold text-[var(--tenant-primary,#005488)] cursor-pointer" onClick={onEdit}>
            Edit
          </button>
        )}
      </div>

      {editing && (
        <div role="group" aria-label="Gateway" className="inline-flex self-start gap-0.5 p-[3px] rounded-[10px] bg-[var(--line-subtle)]">
          {(['cardnet', 'cybersource'] as const).map((g) => (
            <button
              key={g}
              type="button"
              aria-pressed={gateway === g}
              onClick={() => onChange({ gateway: g })}
              className={`h-[34px] px-4 rounded-lg text-sm font-semibold border-0 cursor-pointer ${gateway === g
                ? 'bg-white text-[var(--ink-900)] shadow-sm'
                : 'bg-transparent text-[var(--ink-500)]'}`}
            >
              {GATEWAY_LABEL[g]}
            </button>
          ))}
        </div>
      )}

      {!editing && (
        <div className={row}>
          <span className="text-[var(--ink-500)]">Gateway</span>
          <span>{gateway ? GATEWAY_LABEL[gateway] : <NotSet />}</span>
        </div>
      )}

      {gateway === 'cybersource' ? (
        <>
          <div className={row}>
            <span className="text-[var(--ink-500)]">Secret name</span>
            <span>{configsFailed ? '—' : secret ? <span className="tracking-[1px]">{maskTail(secret.secret_name, 10)}</span> : <NotSet />}</span>
          </div>
          <div className={row}>
            <span className="text-[var(--ink-500)]">Mode</span>
            <span>{secret?.gateway_mode ? (secret.gateway_mode === 'live' ? 'Live' : 'Test') : '—'}</span>
          </div>
          {own.length > 1 && (
            <div className={row}>
              <span className="text-[var(--ink-500)]">Environment</span>
              <span>{own.map((c) => c.environment).join(' · ')}</span>
            </div>
          )}
          <div className={row}>
            <span className="text-[var(--ink-500)]">Status</span>
            <span>{configsFailed ? '—' : <Chip status={cybersourceStatus(!!secret)} />}</span>
          </div>
          <div className="text-[13px] text-[var(--ink-500)]">
            The secret is created by Xhinola in AWS; keys are never entered or shown here.{' '}
            <Link to="/settings/merchant" className="text-[var(--tenant-primary,#005488)] font-semibold">Change it in Merchant configuration</Link>
          </div>
        </>
      ) : gateway === 'cardnet' ? (
        editing ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                Merchant ID
                <input className={input} aria-label="Merchant ID" value={merchantId} placeholder="e.g. 349089090" onChange={(e) => onChange({ merchantId: e.target.value })} />
              </label>
              <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                Terminal ID
                <input className={input} aria-label="Terminal ID" value={terminalId} placeholder="e.g. 10310758" onChange={(e) => onChange({ terminalId: e.target.value })} />
              </label>
            </div>
            <div className="text-[13px] text-[var(--ink-500)]">With both set, services in this business can charge with Cardnet.</div>
          </>
        ) : (
          <>
            <div className={row}>
              <span className="text-[var(--ink-500)]">Merchant ID</span>
              <span>{merchantId.trim() ? <span className="tracking-[1px]">{maskTail(merchantId)}</span> : <NotSet />}</span>
            </div>
            <div className={row}>
              <span className="text-[var(--ink-500)]">Terminal ID</span>
              <span>{terminalId.trim() ? <span className="tracking-[1px]">{maskTail(terminalId)}</span> : <NotSet />}</span>
            </div>
            <div className={row}>
              <span className="text-[var(--ink-500)]">Status</span>
              <span><Chip status={cardnetStatus(merchantId, terminalId)} /></span>
            </div>
          </>
        )
      ) : (
        <div className="text-[13px] text-[var(--ink-500)]">
          {editing ? 'Choose a gateway.' : 'This business names no gateway yet. Choose one with Edit.'}
        </div>
      )}
    </section>
  );
};
